import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { blogPosts, type BlogPost } from '@/mocks/blogs';
import { useSEO } from '@/hooks/useSEO';
import ScrollBatteryBar from '@/components/feature/ScrollBatteryBar';
import SiteHeader from '@/components/feature/SiteHeader';
import Footer from '@/pages/home/components/Footer';

const SORT_OPTIONS = [
  { key: 'relevance', label: 'Best Match' },
  { key: 'date', label: 'Newest First' },
  { key: 'views', label: 'Most Viewed' },
  { key: 'likes', label: 'Most Liked' },
];

function highlight(text: string, query: string): string {
  if (!query.trim()) return text;
  const escaped = query.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return text.replace(new RegExp(`(${escaped})`, 'gi'), '<mark class="bg-amber-200/70 text-amber-900 rounded px-0.5">$1</mark>');
}

function scorePost(post: BlogPost, q: string): number {
  const lq = q.toLowerCase();
  let score = 0;
  if (post.title.toLowerCase().includes(lq)) score += 10;
  if (post.excerpt.toLowerCase().includes(lq)) score += 5;
  if ((post.content ?? '').toLowerCase().includes(lq)) score += 3;
  if (post.category.toLowerCase().includes(lq)) score += 4;
  if (post.author.toLowerCase().includes(lq)) score += 3;
  post.tags.forEach((t) => { if (t.toLowerCase().includes(lq)) score += 6; });
  return score;
}

function SearchResultCard({
  post,
  query,
  onClick,
}: {
  post: BlogPost;
  query: string;
  onClick: () => void;
}) {
  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const formatViews = (v: number) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${v}`);

  const titleHtml = highlight(post.title, query);
  const excerptHtml = highlight(post.excerpt, query);

  return (
    <article
      className="group bg-white/55 backdrop-blur-md rounded-2xl border border-white/80 overflow-hidden cursor-pointer hover:border-[#0088CC]/25 transition-all flex gap-0"
      onClick={onClick}
    >
      {/* Cover image */}
      <div className="w-40 sm:w-52 shrink-0 overflow-hidden">
        <img
          src={post.coverImage}
          alt={post.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>

      {/* Content */}
      <div className="flex flex-col flex-1 min-w-0 p-4 md:p-5">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] font-semibold text-[#0088CC] bg-[#0088CC]/10 px-2 py-0.5 rounded-full uppercase tracking-wider whitespace-nowrap">
            {post.category}
          </span>
          {post.featured && (
            <span className="text-[10px] font-semibold text-amber-600 bg-amber-50 border border-amber-200/60 px-2 py-0.5 rounded-full whitespace-nowrap">
              Featured
            </span>
          )}
        </div>

        <h3
          className="text-sm md:text-base font-bold text-slate-800 leading-snug mb-2 group-hover:text-[#0088CC] transition-colors line-clamp-2"
          dangerouslySetInnerHTML={{ __html: titleHtml }}
        />

        <p
          className="text-xs text-slate-500 line-clamp-2 mb-3 flex-1"
          dangerouslySetInnerHTML={{ __html: excerptHtml }}
        />

        {/* Tags */}
        <div className="flex gap-1 mb-3 flex-wrap">
          {post.tags.map((tag) => (
            <span
              key={tag}
              className={`text-[9px] px-1.5 py-0.5 rounded-full whitespace-nowrap ${
                query && tag.toLowerCase().includes(query.toLowerCase())
                  ? 'bg-amber-100 border border-amber-300/60 text-amber-700'
                  : 'text-slate-400 bg-slate-100/80 border border-slate-200/60'
              }`}
            >
              #{tag}
            </span>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-2 pt-2 border-t border-slate-100/70">
          <img
            src={post.authorAvatar}
            alt={post.author}
            className="w-5 h-5 rounded-full object-cover object-top border border-white/80 shrink-0"
          />
          <span className="text-[10px] text-slate-500 font-medium truncate flex-1 min-w-0">{post.author}</span>
          <div className="flex items-center gap-2.5 shrink-0 text-[10px] text-slate-400">
            {post.views !== undefined && (
              <span className="flex items-center gap-0.5">
                <i className="ri-eye-line text-[11px]"></i>
                {formatViews(post.views)}
              </span>
            )}
            <span className="flex items-center gap-0.5">
              <i className="ri-time-line text-[11px]"></i>
              {post.readTime}m
            </span>
            <span>{formatDate(post.publishedAt)}</span>
          </div>
        </div>
      </div>
    </article>
  );
}

function EmptyState({ query }: { query: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <div className="w-16 h-16 flex items-center justify-center rounded-full bg-slate-100/80 mb-4">
        <i className="ri-search-line text-2xl text-slate-300"></i>
      </div>
      <p className="text-slate-600 font-semibold text-base mb-1">No results for &ldquo;{query}&rdquo;</p>
      <p className="text-slate-400 text-sm max-w-sm">
        Try different keywords, check spelling, or browse all articles below.
      </p>
    </div>
  );
}

export default function SearchPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQ = searchParams.get('q') ?? '';

  const [inputValue, setInputValue] = useState(initialQ);
  const [query, setQuery] = useState(initialQ);

  useSEO({
    title: query ? `Search: "${query}" | TGDirectory Blog` : 'Search Articles | TGDirectory Blog',
    description: 'Search Telegram community guides, rankings, and insights on TGDirectory Blog.',
    keywords: query ? `${query}, Telegram community, TGDirectory search` : 'Telegram community search, TGDirectory blog',
    canonical: 'https://tgdirectory.app/search',
  });
  const [sortKey, setSortKey] = useState('relevance');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync query → URL
  useEffect(() => {
    const t = setTimeout(() => {
      if (query.trim()) {
        setSearchParams({ q: query.trim() });
      } else {
        setSearchParams({});
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, setSearchParams]);

  // Auto-focus on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const allTags = useMemo(() => {
    const s = new Set<string>();
    blogPosts.forEach((p) => p.tags.forEach((t) => s.add(t)));
    return Array.from(s);
  }, []);

  const results = useMemo(() => {
    let posts = [...blogPosts];

    // Filter by keyword
    if (query.trim()) {
      posts = posts
        .map((p) => ({ post: p, score: scorePost(p, query.trim()) }))
        .filter(({ score }) => score > 0)
        .sort((a, b) => {
          if (sortKey === 'relevance') return b.score - a.score;
          if (sortKey === 'date') return new Date(b.post.publishedAt).getTime() - new Date(a.post.publishedAt).getTime();
          if (sortKey === 'views') return (b.post.views ?? 0) - (a.post.views ?? 0);
          if (sortKey === 'likes') return (b.post.likes ?? 0) - (a.post.likes ?? 0);
          return 0;
        })
        .map(({ post }) => post);
    } else {
      // No query: show all, sort by selected key
      posts.sort((a, b) => {
        if (sortKey === 'date') return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
        if (sortKey === 'views') return (b.views ?? 0) - (a.views ?? 0);
        if (sortKey === 'likes') return (b.likes ?? 0) - (a.likes ?? 0);
        return 0;
      });
    }

    // Filter by tag
    if (selectedTag) {
      posts = posts.filter((p) => p.tags.includes(selectedTag));
    }

    return posts;
  }, [query, sortKey, selectedTag]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setQuery(inputValue.trim());
  };

  const handleClear = () => {
    setInputValue('');
    setQuery('');
    inputRef.current?.focus();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f8fb] via-[#f8fafc] to-[#f3f7fa] text-slate-800 font-sans">
      <ScrollBatteryBar />
      <SiteHeader showBack />

      <main className="max-w-screen-xl mx-auto px-6 md:px-10 lg:px-14 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-slate-400 mb-6">
          <button onClick={() => navigate('/')} className="hover:text-slate-600 transition-colors cursor-pointer whitespace-nowrap">Home</button>
          <span>/</span>
          <button onClick={() => navigate('/blogs')} className="hover:text-slate-600 transition-colors cursor-pointer whitespace-nowrap">Blogs</button>
          <span>/</span>
          <span className="text-[#0088CC] font-medium">Search</span>
        </nav>

        {/* Search hero */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl md:text-4xl font-extrabold text-slate-800 mb-2">Search Articles</h1>
          <p className="text-slate-400 text-sm mb-6">Find guides, rankings, and insights from the TGDirectory blog.</p>

          <form onSubmit={handleSubmit} className="max-w-2xl mx-auto">
            <div className="flex items-center bg-white/70 backdrop-blur-md rounded-2xl px-5 py-3.5 border border-[#b8d4ea]/60 focus-within:border-[#0088CC]/50 focus-within:bg-white/90 transition-all gap-3">
              <span className="w-5 h-5 flex items-center justify-center shrink-0">
                <i className="ri-search-line text-slate-400 text-lg"></i>
              </span>
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  setQuery(e.target.value);
                }}
                placeholder="Search by title, tag, author, topic..."
                className="flex-1 bg-transparent text-sm md:text-base text-slate-700 placeholder-slate-400 outline-none min-w-0"
              />
              {inputValue && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="w-6 h-6 flex items-center justify-center shrink-0 text-slate-400 hover:text-slate-600 cursor-pointer rounded-full hover:bg-slate-100/80 transition-colors"
                >
                  <i className="ri-close-line text-base"></i>
                </button>
              )}
              <button
                type="submit"
                className="shrink-0 bg-[#0088CC]/85 hover:bg-[#0088CC] text-white text-sm font-semibold px-4 py-2 rounded-xl whitespace-nowrap transition-colors cursor-pointer"
              >
                Search
              </button>
            </div>
          </form>
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          {/* Sort */}
          <div className="flex items-center gap-1.5 bg-white/55 backdrop-blur-md rounded-xl border border-white/80 p-1">
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                onClick={() => setSortKey(opt.key)}
                className={`text-xs px-3 py-1.5 rounded-lg whitespace-nowrap transition-all cursor-pointer ${
                  sortKey === opt.key
                    ? 'bg-[#0088CC]/80 text-white font-semibold'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-white/60'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Tag filter */}
          <div className="flex flex-wrap gap-1.5 flex-1">
            {allTags.map((tag) => (
              <button
                key={tag}
                onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
                className={`text-[10px] px-2.5 py-1 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                  selectedTag === tag
                    ? 'bg-[#0088CC]/80 border-[#0088CC]/60 text-white'
                    : 'bg-white/50 border-[#b8d4ea]/60 text-slate-500 hover:border-[#0088CC]/30 hover:text-slate-700'
                }`}
              >
                #{tag}
              </button>
            ))}
            {selectedTag && (
              <button
                onClick={() => setSelectedTag(null)}
                className="text-[10px] px-2.5 py-1 rounded-full border border-red-200/60 text-red-400 hover:text-red-600 bg-red-50/60 transition-all cursor-pointer whitespace-nowrap"
              >
                Clear filter
              </button>
            )}
          </div>
        </div>

        {/* Result count */}
        {query.trim() && (
          <div className="mb-4">
            <p className="text-sm text-slate-500">
              {results.length > 0 ? (
                <>
                  Found <strong className="text-slate-700">{results.length}</strong> article{results.length !== 1 ? 's' : ''} for{' '}
                  <strong className="text-[#0088CC]">&ldquo;{query}&rdquo;</strong>
                </>
              ) : null}
            </p>
          </div>
        )}

        {/* Results list */}
        {query.trim() && results.length === 0 ? (
          <EmptyState query={query} />
        ) : (
          <div className="flex flex-col gap-4">
            {results.map((post) => (
              <SearchResultCard
                key={post.id}
                post={post}
                query={query}
                onClick={() => navigate(`/blog/${post.id}`)}
              />
            ))}
          </div>
        )}

        {/* Empty query → show all posts hint */}
        {!query.trim() && results.length > 0 && (
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xs text-slate-400">Showing all articles</span>
            <span className="text-xs text-slate-300">·</span>
            <span className="text-xs text-slate-400">{results.length} posts</span>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
